import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { type ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

/**
 * A camada de dados da assinatura digital e do login gov.br.
 *
 * As quatro telas da ONDA-T4 leem das mesmas três tabelas — a política por tipo
 * de documento, os provedores aceitos em cada política, e o selo exigido por
 * operação de login. Uma camada só, pelo mesmo motivo das configurações: fosse
 * um arquivo por tela, a consulta apareceria quatro vezes (CLAUDE.md, "Regras
 * de interface", item 1).
 *
 * NÃO CONFUNDIR COM O CONECTOR gov.br DE `/admin/integracoes`. Aquele é a
 * parametrização do provedor de identidade de verdade — endereço, cliente,
 * segredo, teste de conexão — e fala com a API. Este aqui é regra de negócio:
 * qual selo o cidadão precisa ter para entrar, e qual nível jurídico cada
 * documento exige para ser assinado.
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
 * `/politica-assinatura`, `/provedores-assinatura` e `/govbr-operacoes` ainda
 * não existem na API. As três tabelas existem no banco desde a migração
 * `0002_dominio_base.sql` (`politica_assinatura_documento`,
 * `provedores_assinatura_aceitos`, `govbr_operacoes_config`), mas nenhuma rota
 * as expõe e não há política de escrita. Decisão do dono de 2026-08-31
 * (CLAUDE.md, "Proibido", exceção estendida) — mesmas três condições de sempre:
 * só leitura, dado claramente fictício, removido antes de `develop` → `hml`
 * (`git grep -n "TEMPORÁRIO" apps/web/src`, checklist em `docs/ALVOS.md`).
 * Nenhum "Salvar" destas telas grava coisa alguma.
 */

/* ------------------------------------------------------------------ *
 * Tipos enumerados, espelhados do banco
 * ------------------------------------------------------------------ */

/**
 * O nível jurídico de uma assinatura, na Lei 14.063/2020.
 *
 * Espelha `nivel_assinatura` (`0002_dominio_base.sql`).
 */
export const NIVEIS_DE_ASSINATURA = ['simples', 'avancada', 'qualificada'] as const;
export type NivelDeAssinatura = (typeof NIVEIS_DE_ASSINATURA)[number];

export const ROTULO_DO_NIVEL: Record<NivelDeAssinatura, string> = {
  simples: 'Simples',
  avancada: 'Avançada',
  qualificada: 'Qualificada',
};

/** Espelha `provedor_assinatura`. */
export const PROVEDORES_DE_ASSINATURA = [
  'govbr_assinador',
  'icpbrasil_a1',
  'icpbrasil_a3',
  'aceite_eletronico',
] as const;
export type ProvedorDeAssinatura = (typeof PROVEDORES_DE_ASSINATURA)[number];

export const ROTULO_DO_PROVEDOR: Record<ProvedorDeAssinatura, string> = {
  govbr_assinador: 'Gov.br Assinador',
  icpbrasil_a1: 'ICP-Brasil A1',
  icpbrasil_a3: 'ICP-Brasil A3',
  aceite_eletronico: 'Aceite eletrônico',
};

/** Espelha `tipo_documento_assinavel`. */
export const TIPOS_DE_DOCUMENTO = [
  'termo',
  'aditivo',
  'distrato',
  'contrato',
  'declaracao',
  'parecer',
] as const;
export type TipoDeDocumento = (typeof TIPOS_DE_DOCUMENTO)[number];

export const ROTULO_DO_TIPO: Record<TipoDeDocumento, string> = {
  termo: 'Termo de adesão',
  aditivo: 'Aditivo',
  distrato: 'Distrato',
  contrato: 'Contrato',
  declaracao: 'Declaração',
  parecer: 'Parecer técnico',
};

/** Espelha `govbr_selo`. */
export const SELOS_GOVBR = ['bronze', 'prata', 'ouro'] as const;
export type SeloGovBr = (typeof SELOS_GOVBR)[number];

export const ROTULO_DO_SELO: Record<SeloGovBr, string> = {
  bronze: 'Bronze',
  prata: 'Prata',
  ouro: 'Ouro',
};

/** O que cada selo significa, para a tela explicar em vez de só nomear. */
export const SIGNIFICADO_DO_SELO: Record<SeloGovBr, string> = {
  bronze: 'Cadastro básico: CPF e senha.',
  prata: 'Validação biométrica ou por dados bancários.',
  ouro: 'Biometria facial presencial — o nível mais alto.',
};

/** Espelha `govbr_operacao`. */
export const OPERACOES_GOVBR = [
  'login_portal_credenciado',
  'login_portal_admin',
  'assinatura_termo',
  'assinatura_aditivo',
  'assinatura_distrato',
  'inscricao_publica',
  'consulta_publica',
] as const;
export type OperacaoGovBr = (typeof OPERACOES_GOVBR)[number];

export const ROTULO_DA_OPERACAO: Record<OperacaoGovBr, string> = {
  login_portal_credenciado: 'Login no portal do credenciado',
  login_portal_admin: 'Login no portal administrativo',
  assinatura_termo: 'Assinatura de termo',
  assinatura_aditivo: 'Assinatura de aditivo',
  assinatura_distrato: 'Assinatura de distrato',
  inscricao_publica: 'Inscrição pública',
  consulta_publica: 'Consulta pública',
};

/* ------------------------------------------------------------------ *
 * Contratos
 * ------------------------------------------------------------------ */

/** A política de assinatura de um tipo de documento. */
export interface PoliticaDeAssinatura {
  id: string;
  tipo_documento: TipoDeDocumento;
  nivel_minimo: NivelDeAssinatura;
  selo_govbr_minimo: SeloGovBr | null;
  base_legal: string;
  observacoes: string | null;
  ativo: boolean;
}

/** Um provedor aceito por uma política, com a ordem em que aparece ao cidadão. */
export interface ProvedorAceito {
  id: string;
  politica_id: string;
  provedor: ProvedorDeAssinatura;
  prioridade: number;
  observacoes: string | null;
  ativo: boolean;
}

/** O selo exigido numa operação de login. */
export interface OperacaoConfigurada {
  id: string;
  operacao: OperacaoGovBr;
  selo_minimo: SeloGovBr;
  ativo: boolean;
  descricao: string | null;
  base_legal: string | null;
  atualizado_em: string;
}

export const CHAVES_DE_ASSINATURA = {
  politicas: ['assinatura', 'politicas'] as const,
  provedores: ['assinatura', 'provedores'] as const,
  operacoesGovbr: ['govbr', 'operacoes'] as const,
};

/* ------------------------------------------------------------------ *
 * Regra de negócio — funciona de verdade, sem API
 * ------------------------------------------------------------------ */

/**
 * Que nível jurídico cada provedor consegue emitir.
 *
 * Não é parametrizável: decorre do que cada tecnologia de certificado é,
 * juridicamente. Aceite eletrônico é assinatura simples; o Assinador gov.br
 * emite avançada; certificado ICP-Brasil, seja A1 ou A3, emite qualificada.
 * Fonte: manual de integração da assinatura eletrônica do gov.br, e a
 * Lei 14.063/2020, art. 4º.
 */
export const NIVEIS_QUE_O_PROVEDOR_EMITE: Record<ProvedorDeAssinatura, NivelDeAssinatura[]> = {
  aceite_eletronico: ['simples'],
  govbr_assinador: ['avancada'],
  icpbrasil_a1: ['qualificada'],
  icpbrasil_a3: ['qualificada'],
};

/** Se um provedor consegue atender ao nível exigido pela política. */
export function provedorAtendeNivel(
  provedor: ProvedorDeAssinatura,
  nivel: NivelDeAssinatura,
): boolean {
  return NIVEIS_QUE_O_PROVEDOR_EMITE[provedor].includes(nivel);
}

export type SituacaoDaPolitica = 'valida' | 'aviso' | 'invalida';

export interface ResultadoDaValidacao {
  situacao: SituacaoDaPolitica;
  mensagens: string[];
}

/**
 * Confere se uma política e seus provedores formam uma configuração aplicável.
 *
 * Função pura, sem API — é a mesma conferência que a régua de alçadas faz com
 * faixas de valor: cruza o que foi parametrizado e diz onde o desenho não fecha.
 * Por isso o diagnóstico das telas funciona de verdade, ao contrário do
 * "Salvar".
 *
 * Três defeitos que ela encontra:
 *
 * 1. política ativa sem nenhum provedor ativo — o documento não pode ser
 *    assinado por ninguém;
 * 2. provedores ativos, mas nenhum capaz do nível exigido — mesma consequência,
 *    por outro caminho;
 * 3. Assinador gov.br com selo mínimo bronze — a API do gov.br recusa a
 *    assinatura com 403, e a tela avisa antes de alguém descobrir em produção.
 */
export function validarPolitica(
  politica: PoliticaDeAssinatura,
  provedores: ProvedorAceito[],
): ResultadoDaValidacao {
  const ativos = provedores.filter((provedor) => provedor.ativo);
  const mensagens: string[] = [];
  let situacao: SituacaoDaPolitica = 'valida';

  if (politica.ativo) {
    if (ativos.length === 0) {
      mensagens.push(
        'Nenhum provedor de assinatura está ativo — este documento não poderá ser assinado.',
      );
      situacao = 'invalida';
    } else if (
      !ativos.some((provedor) => provedorAtendeNivel(provedor.provedor, politica.nivel_minimo))
    ) {
      mensagens.push(
        `O nível exigido é ${ROTULO_DO_NIVEL[politica.nivel_minimo].toLowerCase()}, e nenhum provedor ativo emite esse nível.`,
      );
      situacao = 'invalida';
    }
  }

  const usaGovbr = ativos.some((provedor) => provedor.provedor === 'govbr_assinador');

  if (usaGovbr && politica.selo_govbr_minimo === 'bronze') {
    mensagens.push(
      'O selo bronze não permite assinar pelo gov.br — a API recusa a operação. Eleve para prata.',
    );
    situacao = 'invalida';
  }

  if (usaGovbr && !politica.selo_govbr_minimo) {
    mensagens.push(
      'Defina o selo gov.br mínimo, prata ou ouro, para a assinatura aderir à Lei 14.063/2020.',
    );
    if (situacao === 'valida') {
      situacao = 'aviso';
    }
  }

  if (mensagens.length === 0) {
    mensagens.push('Configuração íntegra e aplicável.');
  }

  return { situacao, mensagens };
}

/* ------------------------------------------------------------------ *
 * Consultas
 * ------------------------------------------------------------------ */

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
// Seis políticas, uma por tipo de documento. As bases legais citadas são norma
// pública de verdade; os identificadores e as observações são de exemplo. A
// política de "parecer" está inativa de propósito, e a de "declaração" tem selo
// bronze com gov.br ativo — é o defeito que a tela de política precisa acusar.
const POLITICAS_MOCK: PoliticaDeAssinatura[] = [
  {
    id: 'politica-exemplo-termo',
    tipo_documento: 'termo',
    nivel_minimo: 'avancada',
    selo_govbr_minimo: 'prata',
    base_legal: 'Lei 14.063/2020, art. 5º, II',
    observacoes: 'Termo de adesão ao credenciamento do transporte escolar.',
    ativo: true,
  },
  {
    id: 'politica-exemplo-contrato',
    tipo_documento: 'contrato',
    nivel_minimo: 'qualificada',
    selo_govbr_minimo: 'ouro',
    base_legal: 'Lei 14.063/2020, art. 5º, III',
    observacoes: 'Instrumento contratual exige certificado ICP-Brasil.',
    ativo: true,
  },
  {
    id: 'politica-exemplo-aditivo',
    tipo_documento: 'aditivo',
    nivel_minimo: 'avancada',
    selo_govbr_minimo: 'prata',
    base_legal: 'Lei 14.063/2020, art. 5º, II',
    observacoes: null,
    ativo: true,
  },
  {
    id: 'politica-exemplo-distrato',
    tipo_documento: 'distrato',
    nivel_minimo: 'avancada',
    selo_govbr_minimo: 'prata',
    base_legal: 'Lei 14.063/2020, art. 5º, II',
    observacoes: null,
    ativo: true,
  },
  {
    id: 'politica-exemplo-declaracao',
    tipo_documento: 'declaracao',
    nivel_minimo: 'simples',
    selo_govbr_minimo: 'bronze',
    base_legal: 'Lei 14.063/2020, art. 4º, I',
    observacoes: 'Declaração de ciência, sem efeito contratual.',
    ativo: true,
  },
  {
    id: 'politica-exemplo-parecer',
    tipo_documento: 'parecer',
    nivel_minimo: 'avancada',
    selo_govbr_minimo: null,
    base_legal: 'Lei 14.063/2020, art. 5º, II',
    observacoes: 'Parecer técnico interno — política ainda não habilitada.',
    ativo: false,
  },
];

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
// Provedores por política. O termo tem gov.br em primeiro e ICP em segundo; a
// declaração tem gov.br ativo com selo bronze (defeito proposital); o parecer
// não tem provedor nenhum, para exercitar o estado vazio.
const PROVEDORES_MOCK: ProvedorAceito[] = [
  {
    id: 'provedor-exemplo-01',
    politica_id: 'politica-exemplo-termo',
    provedor: 'govbr_assinador',
    prioridade: 1,
    observacoes: 'Opção padrão, sem custo para o credenciado.',
    ativo: true,
  },
  {
    id: 'provedor-exemplo-02',
    politica_id: 'politica-exemplo-termo',
    provedor: 'icpbrasil_a3',
    prioridade: 2,
    observacoes: 'Para quem já tem certificado em token ou cartão.',
    ativo: false,
  },
  {
    id: 'provedor-exemplo-03',
    politica_id: 'politica-exemplo-contrato',
    provedor: 'icpbrasil_a1',
    prioridade: 1,
    observacoes: 'Certificado em arquivo.',
    ativo: true,
  },
  {
    id: 'provedor-exemplo-04',
    politica_id: 'politica-exemplo-contrato',
    provedor: 'icpbrasil_a3',
    prioridade: 2,
    observacoes: null,
    ativo: true,
  },
  {
    id: 'provedor-exemplo-05',
    politica_id: 'politica-exemplo-contrato',
    provedor: 'govbr_assinador',
    prioridade: 3,
    observacoes: 'Não emite assinatura qualificada.',
    ativo: false,
  },
  {
    id: 'provedor-exemplo-06',
    politica_id: 'politica-exemplo-aditivo',
    provedor: 'govbr_assinador',
    prioridade: 1,
    observacoes: null,
    ativo: true,
  },
  {
    id: 'provedor-exemplo-07',
    politica_id: 'politica-exemplo-distrato',
    provedor: 'govbr_assinador',
    prioridade: 1,
    observacoes: null,
    ativo: true,
  },
  {
    id: 'provedor-exemplo-08',
    politica_id: 'politica-exemplo-declaracao',
    provedor: 'aceite_eletronico',
    prioridade: 1,
    observacoes: 'Clique de aceite, com endereço de rede e carimbo de tempo.',
    ativo: true,
  },
  {
    id: 'provedor-exemplo-09',
    politica_id: 'politica-exemplo-declaracao',
    provedor: 'govbr_assinador',
    prioridade: 2,
    observacoes: null,
    ativo: true,
  },
];

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
// As sete operações do tipo enumerado, com selo de exemplo. A inscrição pública
// está em bronze e ativa, que é a configuração permissiva que a tela avisa.
const OPERACOES_MOCK: OperacaoConfigurada[] = [
  {
    id: 'operacao-exemplo-01',
    operacao: 'login_portal_credenciado',
    selo_minimo: 'prata',
    ativo: true,
    descricao: 'Acesso do transportador credenciado à área logada.',
    base_legal: 'Decreto 10.543/2020, art. 5º',
    atualizado_em: '2026-08-20T14:30:00Z',
  },
  {
    id: 'operacao-exemplo-02',
    operacao: 'login_portal_admin',
    selo_minimo: 'ouro',
    ativo: false,
    descricao: 'Acesso do servidor à área administrativa — hoje por Keycloak.',
    base_legal: null,
    atualizado_em: '2026-08-18T09:15:00Z',
  },
  {
    id: 'operacao-exemplo-03',
    operacao: 'assinatura_termo',
    selo_minimo: 'prata',
    ativo: true,
    descricao: 'Identidade exigida ao assinar o termo de adesão.',
    base_legal: 'Lei 14.063/2020, art. 5º, II',
    atualizado_em: '2026-08-20T14:30:00Z',
  },
  {
    id: 'operacao-exemplo-04',
    operacao: 'assinatura_aditivo',
    selo_minimo: 'prata',
    ativo: true,
    descricao: null,
    base_legal: 'Lei 14.063/2020, art. 5º, II',
    atualizado_em: '2026-08-20T14:30:00Z',
  },
  {
    id: 'operacao-exemplo-05',
    operacao: 'assinatura_distrato',
    selo_minimo: 'prata',
    ativo: true,
    descricao: null,
    base_legal: 'Lei 14.063/2020, art. 5º, II',
    atualizado_em: '2026-08-20T14:30:00Z',
  },
  {
    id: 'operacao-exemplo-06',
    operacao: 'inscricao_publica',
    selo_minimo: 'bronze',
    ativo: true,
    descricao: 'Inscrição do candidato a credenciamento, no portal público.',
    base_legal: 'Decreto 10.543/2020, art. 4º',
    atualizado_em: '2026-08-12T11:00:00Z',
  },
  {
    id: 'operacao-exemplo-07',
    operacao: 'consulta_publica',
    selo_minimo: 'bronze',
    ativo: false,
    descricao: 'Consulta de protocolo — aberta, não exige identificação.',
    base_legal: null,
    atualizado_em: '2026-08-12T11:00:00Z',
  },
];

/**
 * As políticas de assinatura, uma por tipo de documento.
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
 */
export function usePoliticasDeAssinatura(): UseQueryResult<PoliticaDeAssinatura[], ErroApi> {
  return useQuery<PoliticaDeAssinatura[], ErroApi>({
    queryKey: CHAVES_DE_ASSINATURA.politicas,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => POLITICAS_MOCK,
    retry: naoInsistirEmRecusa,
  });
}

/**
 * Os provedores aceitos, de todas as políticas.
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
 */
export function useProvedoresAceitos(): UseQueryResult<ProvedorAceito[], ErroApi> {
  return useQuery<ProvedorAceito[], ErroApi>({
    queryKey: CHAVES_DE_ASSINATURA.provedores,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => PROVEDORES_MOCK,
    retry: naoInsistirEmRecusa,
  });
}

/**
 * O selo exigido em cada operação de login gov.br.
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
 */
export function useOperacoesGovBr(): UseQueryResult<OperacaoConfigurada[], ErroApi> {
  return useQuery<OperacaoConfigurada[], ErroApi>({
    queryKey: CHAVES_DE_ASSINATURA.operacoesGovbr,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => OPERACOES_MOCK,
    retry: naoInsistirEmRecusa,
  });
}
